import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import { subHours, subDays } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import type { LinkedAmqpOutlet, OutletHistoryResponse } from '../../../../../services/transformerAmqpService';
import { getOutletHistory } from '../../../../../services/transformerAmqpService';
import { PhaseBarChart } from './PhaseBarChart';

registerLocale('de', de);

interface Props {
  isOpen: boolean;
  outlet: LinkedAmqpOutlet;
  trafoId: string;
  onClose: () => void;
}

export type TimePreset = '24h' | '7d' | '30d';

const getOutletLabel = (outlet: LinkedAmqpOutlet) => {
  const name = outlet.name || outlet.portName || outlet.valuePrefix || 'Outlet';
  const slot = outlet.slot !== null ? `Slot ${outlet.slot}` : null;
  return slot ? `${name} (${slot})` : name;
};

const formatInfoValue = (value: number | null | undefined, unit: string): string => {
  if (value === null || value === undefined) return '-';
  return `${value} ${unit}`;
};

const getPresetStartDate = (preset: TimePreset, now: Date): Date => {
  switch (preset) {
    case '24h': return subHours(now, 24);
    case '7d': return subDays(now, 7);
    case '30d': return subDays(now, 30);
  }
};

export const OutletDetailModal: React.FC<Props> = ({
  isOpen,
  outlet,
  trafoId,
  onClose
}) => {
  const [historyData, setHistoryData] = useState<OutletHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Unified time range state
  const [isLive, setIsLive] = useState(true);
  const [preset, setPreset] = useState<TimePreset>('24h');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Compute effective start/end dates
  const startDate = useMemo(() => {
    if (customStartDate) return customStartDate;
    return getPresetStartDate(preset, now);
  }, [customStartDate, preset, now]);

  const endDate = useMemo(() => {
    if (customEndDate) return customEndDate;
    return now;
  }, [customEndDate, now]);

  // Live mode: refresh "now" every 60s
  useEffect(() => {
    if (!isLive || !isOpen) return;
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => clearInterval(interval);
  }, [isLive, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getOutletHistory(trafoId, outlet.id, '30d');
        setHistoryData(data);
      } catch (err) {
        console.error('Error fetching outlet history:', err);
        setError('Fehler beim Laden der historischen Daten');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [isOpen, trafoId, outlet.id]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLive(true);
      setPreset('24h');
      setCustomStartDate(null);
      setCustomEndDate(null);
      setNow(new Date());
    }
  }, [isOpen]);

  const handlePresetClick = useCallback((p: TimePreset) => {
    setPreset(p);
    setCustomStartDate(null);
    setCustomEndDate(null);
    // Keep live mode if it was active, or activate it
    setIsLive(true);
    setNow(new Date());
  }, []);

  const handleLiveToggle = useCallback(() => {
    setIsLive(true);
    setCustomStartDate(null);
    setCustomEndDate(null);
    setNow(new Date());
  }, []);

  const handleRangePick = useCallback((dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
    if (start) {
      // Custom range disables live mode
      setIsLive(false);
    }
  }, []);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-9999">
      <div className="bg-white dark:bg-primary-800 rounded-xl shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" role="dialog" aria-labelledby="outlet-detail-title">
        {/* Modal Header */}
        <div className="px-6 py-4 bg-linear-to-r from-info-500/10 dark:from-info-500/20 to-white dark:to-primary-800 border-b border-gray-300 dark:border-primary-600 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-info-500/20 dark:bg-info-500/30 rounded-lg">
              <svg className="w-5 h-5 text-info-500 dark:text-info-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 id="outlet-detail-title" className="text-lg font-semibold text-gray-900 dark:text-primary-50">
                {getOutletLabel(outlet)}
              </h3>
              <p className="text-xs text-gray-500 dark:text-primary-400">
                {outlet.valuePrefix && <span>Prefix: {outlet.valuePrefix}</span>}
                {outlet.deviceFamily && <span className="ml-2">{outlet.deviceFamily}</span>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-primary-400 dark:hover:text-primary-200 rounded-lg hover:bg-gray-100 dark:hover:bg-primary-700"
            aria-label="Schließen"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Outlet Info */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="bg-gray-50 dark:bg-primary-700 px-3 py-2 rounded-lg">
              <span className="text-gray-500 dark:text-primary-400">Sicherung:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-primary-100">
                {formatInfoValue(outlet.fuseSizeA, 'A')}
              </span>
            </div>
            <div className="bg-gray-50 dark:bg-primary-700 px-3 py-2 rounded-lg">
              <span className="text-gray-500 dark:text-primary-400">Nennleistung:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-primary-100">
                {formatInfoValue(outlet.ratingPowerKva, 'kVA')}
              </span>
            </div>
            <div className="bg-gray-50 dark:bg-primary-700 px-3 py-2 rounded-lg">
              <span className="text-gray-500 dark:text-primary-400">Warnung:</span>
              <span className={`ml-2 font-medium ${outlet.thresholdWarningPercent != null ? 'text-warning-600 dark:text-warning-400' : 'text-gray-400 dark:text-primary-500'}`}>
                {outlet.thresholdWarningPercent != null ? `${outlet.thresholdWarningPercent}%` : 'N/A'}
              </span>
            </div>
            <div className="bg-gray-50 dark:bg-primary-700 px-3 py-2 rounded-lg">
              <span className="text-gray-500 dark:text-primary-400">Kritisch:</span>
              <span className={`ml-2 font-medium ${outlet.thresholdCriticalPercent != null ? 'text-error-600 dark:text-error-400' : 'text-gray-400 dark:text-primary-500'}`}>
                {outlet.thresholdCriticalPercent != null ? `${outlet.thresholdCriticalPercent}%` : 'N/A'}
              </span>
            </div>
          </div>

          {/* Unified Time Range Controller */}
          <div className="bg-gray-50 dark:bg-primary-700/50 rounded-lg p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Live Toggle */}
              <button
                onClick={handleLiveToggle}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isLive
                    ? 'bg-success-500/15 dark:bg-success-500/25 text-success-700 dark:text-success-300 border border-success-500/40'
                    : 'bg-gray-100 dark:bg-primary-700 text-gray-700 dark:text-primary-200 hover:bg-gray-200 dark:hover:bg-primary-600 border border-gray-200 dark:border-primary-600'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-success-500 animate-pulse' : 'bg-gray-400 dark:bg-primary-500'}`} />
                Live
              </button>

              {/* Separator */}
              <div className="w-px h-6 bg-gray-300 dark:bg-primary-500" />

              {/* Preset Buttons */}
              {(['24h', '7d', '30d'] as TimePreset[]).map(p => (
                <button
                  key={p}
                  onClick={() => handlePresetClick(p)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    preset === p && !customStartDate
                      ? 'bg-info-500 text-white'
                      : 'bg-gray-100 dark:bg-primary-700 text-gray-700 dark:text-primary-200 hover:bg-gray-200 dark:hover:bg-primary-600 border border-gray-200 dark:border-primary-600'
                  }`}
                >
                  {p === '24h' ? '24h' : p === '7d' ? '7 Tage' : '30 Tage'}
                </button>
              ))}

              {/* Separator */}
              <div className="w-px h-6 bg-gray-300 dark:bg-primary-500" />

              {/* Range DatePicker */}
              <DatePicker
                selectsRange
                startDate={customStartDate}
                endDate={customEndDate}
                onChange={handleRangePick}
                showTimeSelect
                timeIntervals={60}
                timeFormat="HH:mm"
                dateFormat="dd.MM.yyyy HH:mm"
                locale="de"
                placeholderText="Zeitraum wählen..."
                isClearable
                className="px-3 py-1.5 text-sm bg-white dark:bg-primary-600 border border-gray-300 dark:border-primary-500 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500 min-w-[280px]"
                popperClassName="z-10000"
              />
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-gray-500 dark:text-primary-400">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm">Lade Daten...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-error-500 dark:text-error-400">{error}</p>
            </div>
          )}

          {/* Phase Bar Charts */}
          {!isLoading && !error && (
            <PhaseBarChart
              outlet={outlet}
              historyData={historyData}
              startDate={startDate}
              endDate={endDate}
              isLive={isLive}
            />
          )}

        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-primary-700 border-t border-gray-300 dark:border-primary-600 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white dark:bg-primary-600 border border-gray-300 dark:border-primary-500 rounded-lg text-sm font-medium text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-primary-800 focus:ring-gray-500 dark:focus:ring-primary-400 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
